import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import { Phone, Video, MapPin, Check } from "lucide-react";
import { BookingStateBadge, Card, ClientName, clientDisplayName } from "../components/ui";
import {
  GET_SESSION_DETAIL,
  GET_PRIMARY_CUSTOMER,
  GET_PROVIDERS,
  GET_AVAILABILITIES_RANGE,
  MOVE_BOOKING,
} from "../apollo/sessions";
import { formatTimeRange, formatShortDate } from "../utils/sessionTime";

// ── Types ────────────────────────────────────────────────────────────

interface Availability {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  canPhone: boolean;
  canVideo: boolean;
  canInPerson: boolean;
  providerId: string;
  bookedAt: string | null;
}

interface Provider {
  id: string;
  firstName: string;
  lastName: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

const isMethodCompatible = (avail: Availability, method: string) => {
  if (method === "phone") return avail.canPhone;
  if (method === "video") return avail.canVideo;
  if (method === "in-person") return avail.canInPerson;
  return true;
};

// ── Component ────────────────────────────────────────────────────────

export function SessionMove() {
  const { id: bookingId } = useParams();
  const navigate = useNavigate();

  const [reason, setReason] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [error, setError] = useState<string | null>(null);

  // Data
  const { data: bookingData, loading: bookingLoading } = useQuery(GET_SESSION_DETAIL, {
    variables: { id: bookingId },
    skip: !bookingId,
  });

  const { data: customerData } = useQuery(GET_PRIMARY_CUSTOMER, {
    variables: { filter: { bookingId, isPrimary: true } },
    skip: !bookingId,
  });

  const { data: providerData } = useQuery(GET_PROVIDERS, {
    variables: { perPage: 1000, sortField: "lastName", sortOrder: "ASC" },
  });

  // Availabilities: next 30 days
  const today = useMemo(() => new Date(), []);
  const endDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 30);
    return d;
  }, [today]);

  const toDateStr = (d: Date) => d.toISOString().split("T")[0];

  const { data: availData, loading: availLoading } = useQuery(GET_AVAILABILITIES_RANGE, {
    variables: {
      filter: { date: { gte: toDateStr(today), lte: toDateStr(endDate) } },
      page: 0,
      perPage: 1000,
      sortField: "date",
      sortOrder: "ASC",
    },
  });

  const [moveBooking, { loading: submitting }] = useMutation(MOVE_BOOKING);

  const booking = bookingData?.Booking;
  const primaryCustomer = customerData?.allPeople?.[0] ?? null;
  const allProviders: Provider[] = providerData?.allProviders ?? [];
  const availabilities: Availability[] = availData?.allAvailabilities ?? [];

  const providerMap = useMemo(() => {
    const m = new Map<string, Provider>();
    allProviders.forEach((l) => m.set(l.id, l));
    return m;
  }, [allProviders]);

  // Group availabilities by date -> provider
  const groupedAvailabilities = useMemo(() => {
    const unbooked = availabilities.filter(
      (a) => a.bookedAt === null && a.id !== booking?.availabilityId,
    );

    const grouped: Record<
      string,
      {
        date: string;
        providers: Record<string, { provider: Provider; slots: Availability[] }>;
      }
    > = {};

    for (const avail of unbooked) {
      if (!grouped[avail.date]) {
        grouped[avail.date] = { date: avail.date, providers: {} };
      }
      if (!grouped[avail.date].providers[avail.providerId]) {
        const provider = providerMap.get(avail.providerId) ?? {
          id: avail.providerId,
          firstName: "Unknown",
          lastName: "Provider",
        };
        grouped[avail.date].providers[avail.providerId] = { provider, slots: [] };
      }
      grouped[avail.date].providers[avail.providerId].slots.push(avail);
    }

    return grouped;
  }, [availabilities, providerMap, booking]);

  const sortedDates = useMemo(
    () => Object.keys(groupedAvailabilities).sort(),
    [groupedAvailabilities],
  );

  const selectedAvail = availabilities.find((a) => a.id === selectedId) ?? null;
  const selectedProvider = selectedAvail ? providerMap.get(selectedAvail.providerId) : null;

  // Countdown redirect
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate(`/sessions/${bookingId}`);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, countdown, navigate, bookingId]);

  const handleSubmit = async () => {
    if (!selectedId) return;
    setError(null);
    try {
      const result = await moveBooking({
        variables: {
          bookingId,
          newAvailabilityId: selectedId,
          reason: reason || null,
        },
      });
      if (result.data?.moveBooking) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to move booking";
      setError(message);
    }
  };

  // Loading
  if (bookingLoading || !booking) {
    return (
      <div className="px-7 pt-10 pb-20">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-canvas" />
          <div className="h-10 w-72 rounded bg-canvas" />
          <div className="h-64 rounded-lg bg-canvas" />
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="px-7 pt-10 pb-20 max-w-2xl mx-auto">
        <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-sage mx-auto mb-4 flex items-center justify-center">
            <Check size={24} strokeWidth={2.5} className="text-paper-hi" />
          </div>
          <h2 className="font-display text-[24px] font-medium mb-2">Move initiated</h2>
          <p className="text-[14px] text-ink-3 mb-4">
            The booking has been queued for moving. The customer and provider will be
            notified.
          </p>
          <p className="text-[12.5px] text-ink-4">
            Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-7 pt-10 pb-20">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[12.5px] mb-6">
        <Link to="/sessions" className="text-ink-4 hover:text-ink-2">Sessions</Link>
        <span className="text-ink-5">›</span>
        <Link to={`/sessions/${bookingId}`} className="text-ink-4 hover:text-ink-2">
          {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking.primaryCustomerName ?? "Session")}
        </Link>
        <span className="text-ink-5">›</span>
        <span className="text-ink font-medium">Move</span>
      </div>

      <h1 className="font-display text-[32px] font-medium leading-[1.1] tracking-tight mb-6">
        Move session
      </h1>

      <div className="max-w-4xl space-y-4">
        {/* Current booking summary */}
        <Card header={{ title: "Current booking" }}>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {primaryCustomer && (
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Customer
                </div>
                <div className="text-[13px] font-medium">
                  <ClientName customer={primaryCustomer} showSecondary secondaryClassName="text-[11.5px] text-ink-4 font-normal" />
                </div>
                <div className="text-[11.5px] text-ink-4">{primaryCustomer.email}</div>
              </div>
            )}
            {booking.availability && (
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Current time
                </div>
                <div className="text-[13px] font-medium">
                  {formatShortDate(booking.availability.date)}
                </div>
                <div className="text-[11.5px] text-ink-4">
                  {formatTimeRange(
                    booking.availability.date,
                    booking.availability.startTime,
                    booking.availability.endTime,
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Status
              </div>
              <BookingStateBadge state={booking.state} />
            </div>
          </div>
        </Card>

        {/* Reason */}
        <Card header={{ title: "Reason (optional)" }}>
          <div className="p-5">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this session being moved?"
              className="w-full min-h-[80px] p-3 rounded-md bg-paper hair border text-[13px] text-ink-2 placeholder:text-ink-5 focus:outline-none focus:ring-2 focus:ring-tide/30 resize-none"
            />
          </div>
        </Card>

        {/* Availability picker */}
        <Card header={{ title: "Select new time" }}>
          <div className="p-5">
            {availLoading ? (
              <div className="py-8 text-center text-[13px] text-ink-4">
                Loading available slots...
              </div>
            ) : sortedDates.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-ink-4">
                No available slots found in the next 30 days.
              </div>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto space-y-4">
                {sortedDates.map((dateKey) => {
                  const dayData = groupedAvailabilities[dateKey];
                  const providerGroups = Object.values(dayData.providers);

                  return (
                    <div key={dateKey}>
                      <div className="text-[12.5px] font-semibold text-ink-2 sticky top-0 bg-paper-hi py-1 z-10">
                        {formatShortDate(dateKey)}
                      </div>
                      {providerGroups.map((group) => (
                        <div key={group.provider.id} className="mt-2">
                          <div className="text-[11.5px] text-ink-4 mb-1.5">
                            {group.provider.firstName} {group.provider.lastName}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {group.slots
                              .sort((a, b) => a.startTime.localeCompare(b.startTime))
                              .map((slot) => {
                                const isSelected = selectedId === slot.id;
                                const compatible = isMethodCompatible(slot, booking.method);

                                return (
                                  <button
                                    key={slot.id}
                                    onClick={() => compatible && setSelectedId(slot.id)}
                                    disabled={!compatible}
                                    className={`px-3 py-2 rounded-md text-[12.5px] transition ${
                                      isSelected
                                        ? "bg-sage-s border-2 border-sage text-ink font-medium"
                                        : compatible
                                          ? "hair border bg-paper hover:bg-paper-2 text-ink-2"
                                          : "hair border bg-paper text-ink-5 opacity-50 cursor-not-allowed"
                                    }`}
                                  >
                                    <div className="font-medium num">
                                      {formatTimeRange(slot.date, slot.startTime, slot.endTime)}
                                    </div>
                                    <div className="flex items-center gap-1 mt-0.5 justify-center">
                                      {slot.canPhone && <Phone size={10} className="text-ink-4" />}
                                      {slot.canVideo && <Video size={10} className="text-ink-4" />}
                                      {slot.canInPerson && <MapPin size={10} className="text-ink-4" />}
                                    </div>
                                    {!compatible && (
                                      <div className="text-[10px] text-rust mt-0.5">
                                        Incompatible
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                      <div className="hair-b mt-3" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Selected summary */}
        {selectedAvail && (
          <div className="bg-sage-s/50 rounded-lg border-2 border-sage p-4">
            <div className="text-[12.5px] font-semibold text-sage mb-1">Selected new slot</div>
            <div className="text-[14px] font-medium">
              {formatShortDate(selectedAvail.date)} —{" "}
              {formatTimeRange(selectedAvail.date, selectedAvail.startTime, selectedAvail.endTime)}
            </div>
            {selectedProvider && (
              <div className="text-[12.5px] text-ink-3 mt-0.5">
                Provider: {selectedProvider.firstName} {selectedProvider.lastName}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-rust-s rounded-md px-4 py-3 text-[13px] text-rust flex items-start gap-2">
            <span className="font-medium">Error:</span> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
            className="px-5 py-2.5 rounded-md bg-tide text-paper-hi text-[13.5px] font-medium hover:bg-tide-d disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Moving..." : "Confirm move"}
          </button>
          <Link
            to={`/sessions/${bookingId}`}
            className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13.5px] text-ink-2 hover:bg-paper-2 font-medium"
          >
            Go back
          </Link>
        </div>
      </div>
    </div>
  );
}
